import React, { useEffect, useState } from 'react';
import { computePadding } from './BlockRenderer';
import { resolveEmailImageUrl } from '../utils/emailAssetUrl';
import {
  accentLinePaddingWhenHidden,
  HERO_PRESET_IMAGES,
  normalizeHeroButtonRadius,
  resolveAccentLineOptions,
  type HeroContentResolved,
} from '../utils/heroContentUtils';

const LEGACY_HERO_IMAGE_URLS: Record<string, string> = {
  'https://images.unsplash.com/photo-1522202222206-72cc1c482f5a?auto=format&fit=crop&w=1200&q=80':
    HERO_PRESET_IMAGES.split,
  'https://images.unsplash.com/photo-1522202222206-72cc1c482f5a': HERO_PRESET_IMAGES.split,
};

function resolveBuilderImageSrc(src: string): string {
  const trimmed = src.trim();
  if (!trimmed) return '';
  const migrated = LEGACY_HERO_IMAGE_URLS[trimmed] || trimmed;
  if (/^(https?:\/\/|data:|blob:)/i.test(migrated)) return migrated;
  return resolveEmailImageUrl(migrated);
}

const paletteTextClamp: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

const HeroImagePlaceholder: React.FC<{ minHeight?: number; bg?: string }> = ({
  minHeight = 180,
  bg = '#E5E7EB',
}) => (
  <div
    style={{
      width: '100%',
      minHeight,
      backgroundColor: bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
    aria-hidden
  >
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="1.5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  </div>
);

const HeroImage: React.FC<{
  src: string;
  alt?: string;
  fill?: boolean;
  minHeight?: number;
}> = ({ src, alt = 'Hero', fill = false, minHeight = 180 }) => {
  const [failed, setFailed] = useState(false);
  const resolved = resolveBuilderImageSrc(src);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!resolved || failed) {
    return <HeroImagePlaceholder minHeight={minHeight} />;
  }

  return (
    <img
      key={resolved}
      src={resolved}
      alt={alt}
      className="hero-content-image"
      referrerPolicy="no-referrer"
      style={{
        width: '100%',
        height: fill ? '100%' : 'auto',
        minHeight: fill ? minHeight : undefined,
        maxHeight: fill ? minHeight : undefined,
        objectFit: fill ? 'cover' : undefined,
        objectPosition: 'center center',
        display: 'block',
      }}
      onError={() => setFailed(true)}
    />
  );
};

const HeroEyebrow: React.FC<{
  content: any;
  r: HeroContentResolved;
  align: 'left' | 'center' | 'right';
  compact?: boolean;
  light?: boolean;
}> = ({ content, r, align, compact = false, light = false }) => {
  if (!content.subtitle) return null;
  const eyebrowColor =
    (content.eyebrowColor as string) ||
    (light ? 'rgba(255,255,255,0.75)' : r.textColor);
  return (
    <p
      className="hero-content-eyebrow"
      style={{
        margin: compact ? '0 0 4px 0' : '0 0 10px 0',
        fontSize: compact ? '9px' : '11px',
        fontWeight: 600,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: eyebrowColor,
        fontFamily: r.fontFamily,
        textAlign: align,
        lineHeight: 1.2,
      }}
    >
      {String(content.subtitle)}
    </p>
  );
};

const HeroButton: React.FC<{
  content: any;
  r: HeroContentResolved;
  align: 'left' | 'center' | 'right';
  compact?: boolean;
  lightOutline?: boolean;
}> = ({ content, r, align, compact = false, lightOutline = false }) => {
  if (!content.buttonText || content.showButton === false) return null;
  const radius = normalizeHeroButtonRadius(content.buttonBorderRadius, r.buttonRadius);
  const btnPad = compact ? '5px 10px' : String(content.buttonPadding || '12px 24px');
  const isOutline = content.buttonStyle === 'outline';
  const isGhost = content.buttonStyle === 'ghost';
  const borderColor = lightOutline ? '#FFFFFF' : r.buttonBg;
  const textColor = lightOutline
    ? '#FFFFFF'
    : isOutline || isGhost
      ? r.buttonBg
      : r.buttonColor;

  return (
    <div className="hero-content-button-wrap" style={{ textAlign: align }}>
      <a
        href={String(content.buttonUrl || '#')}
        className="hero-content-button"
        onClick={(e) => e.preventDefault()}
        style={{
          display: 'inline-block',
          backgroundColor: isOutline || isGhost ? 'transparent' : r.buttonBg,
          color: textColor,
          border: isOutline ? `1px solid ${borderColor}` : 'none',
          padding: btnPad,
          borderRadius: radius,
          fontSize: r.buttonFontSize,
          fontWeight: 600,
          textDecoration: isGhost ? 'underline' : 'none',
          fontFamily: r.fontFamily,
          lineHeight: 1.2,
        }}
      >
        {String(content.buttonText)}
      </a>
    </div>
  );
};

type ExtendedProps = {
  template: 'centered-light' | 'banner-image' | 'accent-line' | 'overlay-minimal';
  content: any;
  styles: any;
  r: HeroContentResolved;
  image: string;
  isMobile: boolean;
  isPalettePreview: boolean;
};

export const HeroContentExtendedBlock: React.FC<ExtendedProps> = ({
  template,
  content,
  styles,
  r,
  image,
  isMobile,
  isPalettePreview,
}) => {
  const wrapClass = (base: string) =>
    `${base}${isPalettePreview ? ' hero-palette-preview' : ''}`;
  const imgSrc = image || HERO_PRESET_IMAGES.banner;
  const overlayH = isPalettePreview ? r.overlayMinHeight : isMobile ? 280 : r.overlayMinHeight;
  const bannerH = isPalettePreview ? r.bannerImageMaxHeight : r.bannerImageMaxHeight;
  const accentColor = (content.accentColor as string) || '#111827';

  if (template === 'centered-light') {
    return (
      <div
        className={wrapClass('hero-content-wrap hero-content-centered-light')}
        style={{
          padding: computePadding(styles, '0px'),
          backgroundColor: r.containerBg,
          fontFamily: r.fontFamily,
          textAlign: 'center',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ padding: r.headingPadding, backgroundColor: r.contentBg }}>
          <HeroEyebrow content={content} r={r} align="center" compact={isPalettePreview} />
          {content.heading && (
            <h1
              className="hero-content-heading"
              style={{
                margin: isPalettePreview ? '0 0 4px 0' : '0 0 12px 0',
                fontSize: r.headingSize,
                fontWeight: r.headingWeight,
                color: r.headingColor,
                fontFamily: r.fontFamily,
                lineHeight: 1.2,
                ...(isPalettePreview ? paletteTextClamp : {}),
              }}
            >
              {String(content.heading)}
            </h1>
          )}
        </div>
        {content.text && (
          <div style={{ padding: r.textPadding, backgroundColor: r.contentBg }}>
            <p
              className="hero-content-text"
              style={{
                margin: 0,
                fontSize: r.textSize,
                color: r.textColor,
                lineHeight: '150%',
                fontFamily: r.fontFamily,
                maxWidth: isPalettePreview ? undefined : 480,
                marginLeft: 'auto',
                marginRight: 'auto',
                ...(isPalettePreview ? paletteTextClamp : {}),
              }}
            >
              {String(content.text)}
            </p>
          </div>
        )}
        {(content.buttonText && content.showButton !== false) && (
          <div style={{ padding: r.buttonSectionPadding, backgroundColor: r.contentBg }}>
            <HeroButton content={content} r={r} align="center" compact={isPalettePreview} />
          </div>
        )}
      </div>
    );
  }

  if (template === 'banner-image') {
    const bannerImgH = isPalettePreview ? bannerH : bannerH;
    return (
      <div
        className={wrapClass('hero-content-wrap hero-content-banner-image')}
        style={{
          padding: computePadding(styles, '0px'),
          backgroundColor: r.containerBg,
          fontFamily: r.fontFamily,
          width: '100%',
          boxSizing: 'border-box',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            overflow: 'hidden',
            maxHeight: bannerImgH,
            backgroundColor: r.splitImageBg,
          }}
        >
          <HeroImage src={imgSrc} fill minHeight={bannerImgH} />
        </div>
        <div style={{ padding: r.headingPadding, backgroundColor: r.contentBg }}>
          {content.heading && (
            <h1
              className="hero-content-heading"
              style={{
                margin: isPalettePreview ? '0 0 4px 0' : '0 0 10px 0',
                fontSize: r.headingSize,
                fontWeight: r.headingWeight,
                color: r.headingColor,
                fontFamily: r.fontFamily,
                lineHeight: 1.2,
                ...(isPalettePreview ? paletteTextClamp : {}),
              }}
            >
              {String(content.heading)}
            </h1>
          )}
        </div>
        {content.text && (
          <div style={{ padding: r.textPadding, backgroundColor: r.contentBg }}>
            <p
              className="hero-content-text"
              style={{
                margin: 0,
                fontSize: r.textSize,
                color: r.textColor,
                lineHeight: '150%',
                fontFamily: r.fontFamily,
                maxWidth: isPalettePreview ? undefined : 440,
                marginLeft: 'auto',
                marginRight: 'auto',
                ...(isPalettePreview ? paletteTextClamp : {}),
              }}
            >
              {String(content.text)}
            </p>
          </div>
        )}
        {(content.buttonText && content.showButton !== false) && (
          <div style={{ padding: r.buttonSectionPadding, backgroundColor: r.contentBg }}>
            <HeroButton content={content} r={r} align="center" compact={isPalettePreview} />
          </div>
        )}
      </div>
    );
  }

  if (template === 'accent-line') {
    const accent = resolveAccentLineOptions(content, isPalettePreview);
    const headingPad = accent.show
      ? r.headingPadding
      : accentLinePaddingWhenHidden(r.headingPadding);
    const textPad = accent.show ? r.textPadding : accentLinePaddingWhenHidden(r.textPadding);
    const buttonPad = accent.show
      ? r.buttonSectionPadding
      : accentLinePaddingWhenHidden(r.buttonSectionPadding);

    return (
      <div
        className={wrapClass('hero-content-wrap hero-content-accent-line')}
        style={{
          padding: computePadding(styles, '0px'),
          backgroundColor: r.containerBg,
          fontFamily: r.fontFamily,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          {accent.show && (
            <div
              className="hero-accent-line-bar"
              style={{
                width: accent.widthPx,
                flexShrink: 0,
                backgroundColor: accentColor,
                marginLeft: isPalettePreview ? 8 : 0,
              }}
              aria-hidden
            />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ padding: headingPad, backgroundColor: r.contentBg }}>
              <HeroEyebrow content={content} r={r} align="left" compact={isPalettePreview} />
              {content.heading && (
                <h1
                  className="hero-content-heading"
                  style={{
                    margin: isPalettePreview ? '0 0 4px 0' : '0 0 10px 0',
                    fontSize: r.headingSize,
                    fontWeight: r.headingWeight,
                    color: r.headingColor,
                    fontFamily: r.fontFamily,
                    lineHeight: 1.2,
                    textAlign: 'left',
                    ...(isPalettePreview ? paletteTextClamp : {}),
                  }}
                >
                  {String(content.heading)}
                </h1>
              )}
            </div>
            {content.text && (
              <div style={{ padding: textPad, backgroundColor: r.contentBg }}>
                <p
                  className="hero-content-text"
                  style={{
                    margin: 0,
                    fontSize: r.textSize,
                    color: r.textColor,
                    lineHeight: '150%',
                    fontFamily: r.fontFamily,
                    textAlign: 'left',
                    ...(isPalettePreview ? paletteTextClamp : {}),
                  }}
                >
                  {String(content.text)}
                </p>
              </div>
            )}
            {(content.buttonText && content.showButton !== false) && (
              <div style={{ padding: buttonPad, backgroundColor: r.contentBg }}>
                <HeroButton content={content} r={r} align="left" compact={isPalettePreview} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // overlay-minimal
  const overlayImg = image || HERO_PRESET_IMAGES.overlay;
  return (
    <div
      className={wrapClass('hero-content-wrap hero-content-overlay-minimal')}
      style={{
        padding: computePadding(styles, '0px'),
        backgroundColor: r.containerBg,
        fontFamily: r.fontFamily,
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        minHeight: overlayH,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
        }}
      >
        <HeroImage src={overlayImg} fill minHeight={overlayH} />
      </div>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          background:
            'linear-gradient(to top, rgba(17,24,39,0.88) 0%, rgba(17,24,39,0.35) 45%, rgba(17,24,39,0.05) 100%)',
        }}
        aria-hidden
      />
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          minHeight: overlayH,
          padding: isPalettePreview ? '0 12px 10px' : isMobile ? '0 20px 28px' : '0 32px 40px',
          boxSizing: 'border-box',
        }}
      >
        <HeroEyebrow
          content={content}
          r={r}
          align="left"
          compact={isPalettePreview}
          light
        />
        {content.heading && (
          <h1
            className="hero-content-heading"
            style={{
              margin: isPalettePreview ? '0 0 4px 0' : '0 0 8px 0',
              fontSize: r.headingSize,
              fontWeight: r.headingWeight,
              color: r.headingColor,
              fontFamily: r.fontFamily,
              lineHeight: 1.15,
              textAlign: 'left',
              ...(isPalettePreview ? paletteTextClamp : {}),
            }}
          >
            {String(content.heading)}
          </h1>
        )}
        {content.text && (
          <p
            className="hero-content-text"
            style={{
              margin: isPalettePreview ? '0 0 6px 0' : '0 0 16px 0',
              fontSize: r.textSize,
              color: r.textColor,
              lineHeight: '150%',
              fontFamily: r.fontFamily,
              textAlign: 'left',
              maxWidth: isPalettePreview ? undefined : 420,
              ...(isPalettePreview ? paletteTextClamp : {}),
            }}
          >
            {String(content.text)}
          </p>
        )}
        {(content.buttonText && content.showButton !== false) && (
          <HeroButton
            content={content}
            r={r}
            align="left"
            compact={isPalettePreview}
            lightOutline
          />
        )}
      </div>
    </div>
  );
};
